import type { ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Disable the confirm button (e.g. while the confirmed action is in flight). */
  confirmDisabled?: boolean;
  /**
   * Rendered above the message — e.g. a `DialogValidationErrors` block so a failed confirm can
   * report inside the dialog and let the user retry or cancel, instead of closing on error.
   */
  children?: ReactNode;
}

export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  children,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent>
        {children}
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{cancelLabel ?? t('common.no', { defaultValue: 'No' })}</Button>
        <Button onClick={onConfirm} variant="contained" color="primary" disabled={confirmDisabled}>
          {confirmLabel ?? t('common.yes', { defaultValue: 'Yes' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
